/**
 * Facebook JavaScript SDK loader — used only by WhatsApp Embedded Signup.
 *
 * Embedded Signup is the one Meta flow that cannot be done with a redirect URL.
 * `config_id` is not honoured on `/dialog/oauth`, and the guided dialog reports
 * its progress (the WABA it created, the number it verified, the step the user
 * abandoned on) through `postMessage` rather than query parameters. Both of
 * those require the SDK to be on the page, so it is loaded here — lazily, and
 * only when someone actually clicks Connect, because this is a third-party
 * script on a marketing domain and no other screen should pay for it.
 */

const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
const SCRIPT_ID = 'facebook-jssdk';
const SDK_TIMEOUT_MS = 15000;

/**
 * The Graph version `FB.init` runs on.
 *
 * Deliberately NOT the server's `WHATSAPP_GRAPH_API`. Tying them together read
 * as tidy and is wrong: the backend pins an old version because changing it
 * touches every server-side Graph call, while the SDK needs a version Facebook
 * still supports — `FB.init` throws "Invalid version specified" on one it has
 * retired, and Embedded Signup only exists on recent versions. They answer to
 * different constraints, so they get different values. The backend may still
 * override this via `graphVersion` when it has a reason to.
 */
const DEFAULT_SDK_VERSION = 'v21.0';

let loadPromise = null;

/**
 * Load and initialise the SDK exactly once per page.
 *
 * The promise is cached rather than the boolean: two rapid clicks would
 * otherwise inject two script tags and race `FB.init`, and the second `init`
 * silently wins with whatever arguments it was given.
 *
 * @param {string} appId       Meta app id, from the backend — never hardcoded,
 *                             because the SA console can override it.
 * @param {string} graphVersion e.g. 'v21.0'
 * @returns {Promise<object>}  the initialised `window.FB`
 */
export function loadFacebookSdk(appId, graphVersion = DEFAULT_SDK_VERSION) {
  if (!appId) return Promise.reject(new Error('Meta App ID is missing.'));
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }

    window.fbAsyncInit = () => {
      try {
        window.FB.init({ appId, cookie: true, xfbml: false, version: graphVersion });
        resolve(window.FB);
      } catch (err) {
        reject(err);
      }
    };

    /**
     * A pending script tag means `fbAsyncInit` is still coming — but if it never
     * arrives (blocked mid-flight, network stall) this promise would hang and
     * the button would sit dead with no error at all. Time it out instead.
     */
    if (document.getElementById(SCRIPT_ID)) {
      setTimeout(() => reject(new Error('Facebook SDK did not initialise in time.')), SDK_TIMEOUT_MS);
      return;
    }

    setTimeout(() => reject(new Error('Facebook SDK did not initialise in time.')), SDK_TIMEOUT_MS);

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SDK_SRC;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => {
      // Reset so a later attempt can retry instead of hanging on a dead promise.
      loadPromise = null;
      reject(new Error('Could not load Facebook. Check your connection or any ad/tracker blocker.'));
    };
    document.body.appendChild(script);
  });

  return loadPromise;
}

/**
 * Listen to the Embedded Signup dialog's progress messages.
 *
 * Meta posts `{ type: 'WA_EMBEDDED_SIGNUP' }` from `facebook.com` as the user
 * moves through the guided flow. It is the only way to learn *why* a signup
 * produced no code — the popup simply closes on cancel, and without this the
 * screen can only say "something went wrong".
 *
 * The origin is checked on every message: this listener runs on the customer's
 * own LMS domain, and anything on the page can post to `window`.
 *
 * @param {(data: object) => void} onEvent
 * @returns {() => void} unsubscribe
 */
export function onEmbeddedSignupEvent(onEvent) {
  const handler = (event) => {
    if (!/^https:\/\/(www\.)?facebook\.com$/.test(event.origin)) return;
    let payload = event.data;
    if (typeof payload === 'string') {
      try { payload = JSON.parse(payload); } catch { return; }
    }
    if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;
    onEvent(payload);
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
